import { useCallback, useContext, useMemo } from "react";
import { DayOfWeek, FirstWeekOfYear } from "@fluentui/react/lib/DateTimeUtilities";

import {
  formatDate, formatDay, formatMonth, formatYear, is12HourFormat
} from "Utils/Date";
import { getCldrInfo, ShortDayNames } from "Utils/CLDR";
import { TranslationContext } from "Components/Generic/Translator";
import { IUseDateConfigProps } from "./IUseDateConfigProps";

export function useDateConfig(): IUseDateConfigProps {
  const translator = useContext(TranslationContext);
  const activeLocale = translator.getActiveLocale();

  const [days, shortDays] = useMemo(() => {
    const strings = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(1970, 0, i + 4);
      return [
        date.toLocaleString(activeLocale, { weekday: `long` }),
        date.toLocaleString(activeLocale, { weekday: `narrow` })
      ];
    });

    return [strings.map(([short]) => short), strings.map(([, long]) => long)];
  }, [activeLocale]);

  const [months, shortMonths] = useMemo(() => {
    const strings = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(1970, i, 1);
      return [
        date.toLocaleString(activeLocale, { month: `long` }),
        date.toLocaleString(activeLocale, { month: `short` })
      ];
    });

    return [strings.map(([short]) => short), strings.map(([, long]) => long)];
  }, [activeLocale]);

  const [firstWeekOfYear, firstDayOfWeek] = useMemo(() => {
    const { minimalDays, firstDay } = getCldrInfo();
    return [getFirstWeekOfYear(minimalDays), getDateLikeDayNumberFromTemporal(firstDay)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translator]);

  const useHour12 = useMemo(() => is12HourFormat(activeLocale), [activeLocale]);

  const dateTimeFormatter = useMemo(
    () => ({
      formatDay: (date) => formatDay(activeLocale, date, `short`),
      formatMonth: (date) => formatMonth(activeLocale, date, `medium`),
      formatYear: (date) => formatYear(activeLocale, date),
      formatMonthYear: (date) => `${formatMonth(activeLocale, date, `full`)} ${formatYear(activeLocale, date)}`,
      formatMonthDayYear: (date) => `${formatYear(activeLocale, date)} ${formatMonth(activeLocale, date)} ${formatDate(activeLocale, date)}`
    }),
    [activeLocale]
  );

  const formatDateInt = useCallback((date?: Date) => formatDate(activeLocale, date, `full`, null), [activeLocale]);

  return {
    dateTimeFormatter,
    firstDayOfWeek,
    firstWeekOfYear,
    formatDate: formatDateInt,
    useHour12,
    strings: {
      days,
      goToToday: translator.get(`calendar.gototoday`, `Go to today~`),
      months,
      shortDays,
      shortMonths
    }
  };
}

function getFirstWeekOfYear(minimalDays: number): FirstWeekOfYear {
  if (minimalDays > 4) {
    return FirstWeekOfYear.FirstFullWeek;
  }

  if (minimalDays > 1) {
    return FirstWeekOfYear.FirstFourDayWeek;
  }

  return FirstWeekOfYear.FirstDay;
}

function getDateLikeDayNumberFromTemporal(firstTemporalDay: ShortDayNames): DayOfWeek {
  const dayOfWeekMap = {
    mon: DayOfWeek.Monday,
    tue: DayOfWeek.Tuesday,
    wen: DayOfWeek.Wednesday,
    thu: DayOfWeek.Thursday,
    fri: DayOfWeek.Friday,
    sat: DayOfWeek.Saturday,
    sun: DayOfWeek.Sunday
  };

  return dayOfWeekMap[firstTemporalDay] ?? DayOfWeek.Monday;
}
